import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Award, Eye, Plus, Send, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminCertificates() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showIssue, setShowIssue] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState("");
    const [selectedCourse, setSelectedCourse] = useState("");
    const [grade, setGrade] = useState("A");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        try {
            const [c, cu, co] = await Promise.all([
                api.get("/admin/certificates"),
                api.get("/admin/customers"),
                api.get("/admin/courses"),
            ]);
            setList(c); setCustomers(cu); setCourses(co);
        } catch { toast.error("Load failed"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const issue = async () => {
        if (!selectedStudent || !selectedCourse) return toast.error("Pick a student and course first");
        const student = customers.find((c) => c.id === selectedStudent);
        try {
            await api.post("/admin/certificates", {
                student_id: student.id,
                student_name: student.name,
                course_id: selectedCourse,
                grade,
            });
            toast.success(`Certificate issued to ${student.name}`);
            setShowIssue(false);
            setSelectedStudent(""); setSelectedCourse(""); setGrade("A");
            load();
        } catch { toast.error("Failed to issue"); }
    };

    return (
        <AppLayout searchPlaceholder="Search certificates...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Certificate Management</h1>
                    <p className="text-sm text-gray-400 mt-2">{list.length} certificates issued • Issue or revoke credentials</p>
                </div>
                <button onClick={() => setShowIssue(true)} data-testid="issue-cert-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                    <Plus className="w-4 h-4" /> Issue Certificate
                </button>
            </div>

            {loading ? <div className="text-gray-500">Loading…</div> : (
                <div className="bg-ink-700 rounded-3xl border border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs uppercase text-gray-500 tracking-widest bg-ink-800">
                                    <th className="text-left font-medium px-6 py-4">Student</th>
                                    <th className="text-left font-medium px-6 py-4">Course</th>
                                    <th className="text-left font-medium px-6 py-4">Code</th>
                                    <th className="text-left font-medium px-6 py-4">Grade</th>
                                    <th className="text-left font-medium px-6 py-4">Issued</th>
                                    <th className="text-right font-medium px-6 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((c) => (
                                    <tr key={c.id} data-testid={`admin-cert-row-${c.id}`} className="border-t border-white/5 hover:bg-ink-800/50">
                                        <td className="px-6 py-4 font-semibold text-sm">{c.student_name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-300">{c.course_title}</td>
                                        <td className="px-6 py-4 text-xs font-mono text-gray-400">{c.code}</td>
                                        <td className="px-6 py-4"><span className="px-3 py-1 rounded-full bg-lime/15 text-lime text-xs font-bold">{c.grade}</span></td>
                                        <td className="px-6 py-4 text-xs text-gray-400">{c.issued_at ? new Date(c.issued_at).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <Link to={`/certificates/${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-grape/15 text-grape hover:bg-grape/25 text-xs font-semibold transition-colors">
                                                    <Eye className="w-3.5 h-3.5" /> View
                                                </Link>
                                                <button data-testid={`revoke-cert-${c.id}`} onClick={() => setConfirmDelete(c)} className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-500 inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showIssue && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={() => setShowIssue(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-ink-700 rounded-3xl border border-white/10 p-7 fade-up">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center"><Award className="w-5 h-5 text-lime" /></div>
                                <div>
                                    <h2 className="font-display font-bold text-xl">Issue Certificate</h2>
                                    <p className="text-xs text-gray-400">Generate a new credential for a student</p>
                                </div>
                            </div>
                            <button onClick={() => setShowIssue(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 mb-2">Student</label>
                                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                                    <SelectTrigger data-testid="issue-student-select" className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 h-12 text-white"><SelectValue placeholder="Select student..." /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border border-white/10 text-white">
                                        {customers.map((c) => <SelectItem key={c.id} value={c.id} className="text-white focus:bg-ink-700 focus:text-white">{c.name} — {c.email}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-2">Course</label>
                                <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                                    <SelectTrigger data-testid="issue-course-select" className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 h-12 text-white"><SelectValue placeholder="Select course..." /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border border-white/10 text-white">
                                        {courses.map((c) => <SelectItem key={c.id} value={c.id} className="text-white focus:bg-ink-700 focus:text-white">{c.title}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-2">Grade</label>
                                <Select value={grade} onValueChange={setGrade}>
                                    <SelectTrigger data-testid="issue-grade-select" className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 h-12 text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border border-white/10 text-white">
                                        {["A+", "A", "A-", "B+", "B", "Pass"].map((g) => <SelectItem key={g} value={g} className="text-white focus:bg-ink-700 focus:text-white">{g}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="mt-7 flex justify-end gap-3">
                            <button onClick={() => setShowIssue(false)} className="px-5 py-2.5 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">Cancel</button>
                            <button onClick={issue} data-testid="confirm-issue" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all">
                                <Send className="w-4 h-4" /> Issue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={() => setConfirmDelete(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-ink-700 rounded-3xl border border-white/10 p-7">
                        <h3 className="font-display font-bold text-xl">Revoke certificate?</h3>
                        <p className="text-sm text-gray-400 mt-2">This will delete the credential <strong>{confirmDelete.code}</strong> for {confirmDelete.student_name}.</p>
                        <div className="mt-5 flex justify-end gap-3">
                            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
                            <button data-testid="confirm-revoke-cert" onClick={async () => {
                                try { await api.del(`/admin/certificates/${confirmDelete.id}`); toast.success("Revoked"); setConfirmDelete(null); load(); }
                                catch { toast.error("Failed"); }
                            }} className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-bold">Revoke</button>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}
